import type { OnInit, OnDestroy} from '@angular/core';
import { Component, inject, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil, finalize, catchError, of } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CustomTooltipDirective } from '../../../shared/directives/custom-tooltip.directive';
import { CycleManagementService } from '../../../core/services/cycle-management.service';
import { SnackbarService } from '../../../core/services/snackbar.service';
import { SharePointService } from '../../../core/services/sharepoint.service';
import { ActivityTabComponent } from '../../manager-form/components/activity-tab/activity-tab.component';
import type { Form } from '../../view-manager-cycle-form/models/view-manager-form.models';
import type { AssignedFormsResponse, AssignedCycleForm, CycleFormQuestionsResponse, CycleFormQuestion, CycleAnswersResponse } from '../../../shared/models/cycle-management.models';

// Local interfaces for component-specific types
interface ActivityTab {
  activity_number: number;
  total_answers: number;
  no_answers: number;
  activity_status: 'draft' | 'submitted' | 'approved' | 'rejected' | null;
  display_name: string;
  reference_number?: string;
}

interface ActivityOverallInput {
  root_cause?: string;
  rootCause?: string;
  rootCauseDescription?: string;
  mitigation_action?: string;
  mitigationAction?: string;
  mitigationActionDescription?: string;
  monitoring_confirmed?: boolean;
  confirmed?: boolean;
  comments?: string;
  approvedBy?: string;
  approverComments?: string;
  decision_status?: string;
  activityStatus?: 'draft' | 'submitted' | 'approved' | 'rejected';
  isActionRequiredOnReject?: boolean;
}

interface QuestionData {
  question_id: number;
  question_number?: number;
  question_text: string;
  question_type: string;
  question_code?: string;
  display_order?: number;
  answer: string | null;
  answer_inputs: Record<string, unknown> | null;
  showQuestion?: boolean;
  // Template aliases
  id: number;
  text: string;
  type: string;
  questionNumber?: number;
  answerText?: string;
  comments?: string;
  attachment?: string;
}

interface StageData {
  stage_id?: number;
  stage_name: string;
  stage_order: number;
  questions: QuestionData[];
  // Template aliases
  id?: number;
  name: string;
}

interface ActivityData {
  activity_number: number;
  display_name: string;
  activity_status: string;
  stages: StageData[];
}

interface EnrichedForm extends Form {
  activities?: ActivityData[];
}

interface ComponentFormData {
  cycleId: string;
  cycleName: string;
  managerId: string;
  managerName: string;
  deadline: string;
  forms: EnrichedForm[];
  showOnlyAnswersWithNo: boolean;
}

/**
 * Sub-Manager Cycle Forms Component
 *
 * Displays view-only cycle forms assigned to a sub-manager with:
 * - Form list sidebar with completion statistics
 * - Activity tabs for multi-activity forms
 * - Read-only question/answer display with filtering
 * - Overall input tracking (root cause, mitigation)
 * - Decision status display (approved/rejected)
 *
 * This component is used by system managers to monitor sub-manager progress
 * and review submitted forms without editing capabilities.
 *
 * Data is pre-loaded via the subManagerCycleFormsResolver which validates
 * access and fetches manager details before component initialization.
 */
@Component({
  selector: 'app-sub-manager-cycle-forms',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatCheckboxModule,
    CustomTooltipDirective,
    ActivityTabComponent
  ],
  templateUrl: './sub-manager-cycle-forms.component.html',
  styleUrls: ['./sub-manager-cycle-forms.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubManagerCycleFormsComponent implements OnInit, OnDestroy {
  // Manager and cycle identifiers (populated by resolver)
  cycleId!: number;
  managerId!: number;
  managerName = '';
  managerPrid = '';

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly cycleManagementService = inject(CycleManagementService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly sharePointService = inject(SharePointService);
  private readonly destroy$ = new Subject<void>();

  // Component state
  isLoading = false;
  error: string | null = null;
  isSidebarCollapsed = false;
  private hasLoadedData = false; // Track if data has been loaded

  // Form data
  formData: ComponentFormData = {
    cycleId: '',
    cycleName: '',
    managerId: '',
    managerName: '',
    deadline: '',
    forms: [],
    showOnlyAnswersWithNo: false
  };

  currentActivityOverallInput: ActivityOverallInput = {};
  currentActivityHasNoAnswer = false;

  selectedForm: EnrichedForm | null = null;
  selectedActivityIndex = 0;
  activityTabs: ActivityTab[] = [];
  totalForms = 0;
  totalActivityOwners = 0;
  overallCompletion = 0;
  totalQuestionsCount = 0;
  managerDeadline = '';
  completionStatusClass = '';

  // Caches
  formCompletedCache = new Map<number, boolean>();
  formNotApplicableCache = new Map<number, boolean>();
  formNoAnswersCache = new Map<number, number>();

  ngOnInit(): void {
    // Get resolved data from router
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
      const resolvedData = data['resolvedData'];

      if (!resolvedData) {
        // Resolver failed, user should have been redirected
        return;
      }

      // Set component properties from resolved data
      this.cycleId = resolvedData.cycleId;
      this.managerId = resolvedData.manager.id;
      this.managerName = `${resolvedData.manager.first_name} ${resolvedData.manager.last_name}`;
      this.managerPrid = resolvedData.manager.prid;

      // Load cycle form data
      this.loadCycleFormData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads cycle form data for the selected manager and cycle
   * Fetches assigned forms and processes them for display
   */
  private loadCycleFormData(): void {
    if (!this.cycleId || !this.managerId) {
      this.error = 'Missing required parameters';
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.hasLoadedData = true;

    this.cycleManagementService
      .getAssignedForms(this.cycleId, this.managerId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        catchError(error => {
          console.error('Error loading cycle form data:', error);
          this.error = 'Failed to load cycle form data';
          this.snackbarService.showError('Failed to load cycle form data');
          return of(null);
        })
      )
      .subscribe((data: AssignedFormsResponse | null) => {
        if (data) {
          this.processFormData(data);
        } else {
          this.error = 'No data received';
        }
      });
  }

  private processFormData(data: AssignedFormsResponse): void {
    // Set cycle name from first form if available
    const cycleName = data.assigned_forms?.length > 0
      ? data.assigned_forms[0].cycle_name || ''
      : '';

    const deadline = data.assigned_forms?.length > 0
      ? data.assigned_forms[0].form_deadline_date || ''
      : '';

    // Transform API response to component format
    const assignedForms = data.assigned_forms || [];
    const transformedForms = assignedForms.map((apiForm: AssignedCycleForm, index: number) => ({
      id: apiForm.cycle_form_id || index + 1,
      name: apiForm.form_name,
      type: 'Form' as const,
      status: apiForm.activity_status || 'draft',
      isSelected: index === 0,
      cycle_form_id: apiForm.cycle_form_id,
      form_code: apiForm.form_code,
      cycle_assignment_id: apiForm.cycle_assignment_id,
      activity_count: apiForm.activity_count || 0,
      completion_percentage: apiForm.completion_percentage || 0,
      no_answers: apiForm.no_answers || 0,
      activity_statistics: apiForm.activity_statistics || [],
      form_deadline_date: apiForm.form_deadline_date || '',
      cycleName: apiForm.cycle_name || '',
      is_form_not_applicable: apiForm.is_form_not_applicable,
      form_not_applicable_reason: apiForm.form_not_applicable_reason,
      cycle_assignment_status: apiForm.cycle_assignment_status,
      activities: [] // Will be loaded when form is selected
    }));

    this.formData = {
      cycleId: this.cycleId.toString(),
      cycleName: cycleName,
      managerId: this.managerId.toString(),
      managerName: this.managerName,
      deadline: deadline,
      forms: transformedForms,
      showOnlyAnswersWithNo: false
    };

    this.totalForms = this.formData.forms?.length || 0;
    this.managerDeadline = deadline;

    // Process completion and cache
    this.formData.forms?.forEach((form) => {
      if (form.is_form_not_applicable) {
        this.formNotApplicableCache.set(form.id, true);
      }
      if (form.completion_percentage === 100) {
        this.formCompletedCache.set(form.id, true);
      }
      if (form.no_answers && form.no_answers > 0) {
        this.formNoAnswersCache.set(form.id, form.no_answers);
      }
    });

    // Calculate overall metrics
    this.calculateOverallMetrics();

    // Select first form if available
    if (this.totalForms > 0) {
      this.selectForm(this.formData.forms[0]);
    }

    this.cdr.markForCheck();
  }

  private calculateOverallMetrics(): void {
    let totalCompletion = 0;
    let formCount = 0;
    let activityCount = 0;

    this.formData.forms?.forEach((form) => {
      if (!form.is_form_not_applicable) {
        totalCompletion += form.completion_percentage || 0;
        formCount++;
      }
      activityCount += form.activity_count || 0;
    });

    this.overallCompletion = formCount > 0 ? Math.round(totalCompletion / formCount) : 0;
    this.totalActivityOwners = activityCount;

    // Set completion status class
    if (this.overallCompletion === 100) {
      this.completionStatusClass = 'complete';
    } else if (this.overallCompletion >= 75) {
      this.completionStatusClass = 'high';
    } else if (this.overallCompletion >= 50) {
      this.completionStatusClass = 'medium';
    } else {
      this.completionStatusClass = 'low';
    }
  }

  selectForm(form: EnrichedForm): void {
    // Deselect previous
    if (this.selectedForm) {
      this.selectedForm.isSelected = false;
    }

    // Select new form
    this.selectedForm = form;
    form.isSelected = true;
    this.selectedActivityIndex = 0;

    // Load form questions and answers
    this.loadFormQuestions(form);

    this.cdr.markForCheck();
  }

  private loadFormQuestions(form: EnrichedForm): void {
    if (!form.cycle_form_id) return;

    const cycleIdNum = Number(this.cycleId);

    this.cycleManagementService.getCycleFormQuestions(cycleIdNum, form.cycle_form_id)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Error loading form questions:', error);
          this.snackbarService.showError('Failed to load form questions');
          return of(null);
        })
      )
      .subscribe((response: CycleFormQuestionsResponse | null) => {
        if (!response) return;

        // Build activities from activity_statistics
        const activityStats = form.activity_statistics || [];
        form.activities = activityStats.map((stat) => ({
          activity_number: stat.activity_number,
          display_name: stat.display_name || `Activity ${stat.activity_number}`,
          activity_status: stat.activity_status || '',
          stages: []
        }));

        // Group questions by stage (questions don't have activity_number)
        const stagesMap = new Map<number | string, StageData>();
        const questions = response.questions || [];

        questions.forEach((apiQuestion: CycleFormQuestion) => {
          const stageKey = apiQuestion.stage_id ?? 'untagged';

          if (!stagesMap.has(stageKey)) {
            stagesMap.set(stageKey, {
              stage_id: apiQuestion.stage_id ?? undefined,
              stage_name: apiQuestion.stage_name || 'General Questions',
              stage_order: apiQuestion.stage_order || 99999,
              questions: [],
              id: apiQuestion.stage_id ?? undefined,
              name: apiQuestion.stage_name || 'General Questions'
            });
          }

          const stage = stagesMap.get(stageKey);
          if (stage) {
            const questionData: QuestionData = {
              question_id: apiQuestion.question_id,
              question_number: apiQuestion.display_order,
              question_text: apiQuestion.question_text_en,
              question_type: apiQuestion.question_type,
              question_code: apiQuestion.question_code ?? undefined,
              display_order: apiQuestion.display_order,
              answer: null,
              answer_inputs: null,
              id: apiQuestion.question_id,
              text: apiQuestion.question_text_en,
              type: apiQuestion.question_type,
              questionNumber: apiQuestion.display_order,
              answerText: undefined,
              comments: undefined,
              attachment: undefined
            };
            stage.questions.push(questionData);
          }
        });

        // Assign stages to all activities (same questions for all activities)
        const stageArray = Array.from(stagesMap.values()).sort((a, b) => a.stage_order - b.stage_order);
        form.activities?.forEach(activity => {
          activity.stages = stageArray.map(s => ({ ...s, questions: [...s.questions] }));
        });

        // Sort questions within each stage
        form.activities?.forEach((activity) => {
          activity.stages.forEach((stage) => {
            stage.questions.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
          });
        });

        // Load answers if assignment exists
        if (form.cycle_assignment_id) {
          this.loadFormAnswers(form);
        } else {
          this.buildActivityTabs(form);
        }

        this.cdr.markForCheck();
      });
  }

  private loadFormAnswers(form: EnrichedForm): void {
    if (!form.cycle_assignment_id) return;

    this.cycleManagementService.getCycleAssignmentAnswers(form.cycle_assignment_id)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Error loading form answers:', error);
          return of(null);
        })
      )
      .subscribe((response: CycleAnswersResponse | null) => {
        if (!response || !response.activities) {
          this.buildActivityTabs(form);
          this.cdr.markForCheck();
          return;
        }

        // Map answers to questions
        response.activities.forEach((apiActivity) => {
          const activity = form.activities?.find((a) =>
            a.activity_number === apiActivity.activity_details.activity_number
          );

          if (activity) {
            activity.activity_status = apiActivity.activity_status.activity_status || '';
            // Filter out "unknown" or "Unknown" values - treat as empty
            const displayName = apiActivity.activity_details.display_name || '';
            activity.display_name = (displayName.toLowerCase() === 'unknown') ? '' : displayName;

            apiActivity.answers.forEach((apiAnswer) => {
              activity.stages?.forEach((stage) => {
                const question = stage.questions?.find((q) =>
                  q.question_id === apiAnswer.question_id
                );
                if (question) {
                  question.answer = apiAnswer.answer || null;
                  question.answerText = apiAnswer.answer || undefined;
                  question.answer_inputs = null as Record<string, unknown> | null; // API doesn't provide answer_inputs in this format
                  // Note: comments and attachment would need to be fetched from answer details if available
                  this.enrichQuestion(question);
                }
              });
            });
          }
        });

        this.buildActivityTabs(form);
        this.cdr.markForCheck();
      });
  }

  private buildActivityTabs(form: EnrichedForm): void {
    this.activityTabs = form.activities?.map((activity) => {
      const status = activity.activity_status;
      const validStatus = (status === 'draft' || status === 'submitted' || status === 'approved' || status === 'rejected')
        ? status
        : null;

      return {
        activity_number: activity.activity_number,
        total_answers: this.countAnsweredQuestions(activity),
        no_answers: this.countNoAnswersInActivity(activity),
        activity_status: validStatus,
        display_name: activity.display_name || `Activity ${activity.activity_number}`
      };
    }) || [];

    this.totalQuestionsCount = this.countTotalQuestions(form);

    // Enrich all questions to set showQuestion flag
    form.activities?.forEach(activity => {
      activity.stages?.forEach(stage => {
        stage.questions?.forEach(question => {
          this.enrichQuestion(question);
        });
      });
    });
  }

  private countAnsweredQuestions(activity: ActivityData): number {
    let count = 0;
    activity.stages?.forEach((stage) => {
      stage.questions?.forEach((question) => {
        if (question.answer) {
          count++;
        }
      });
    });
    return count;
  }

  private countNoAnswersInActivity(activity: ActivityData): number {
    let count = 0;
    activity.stages?.forEach((stage) => {
      stage.questions?.forEach((question) => {
        if (question.answer && question.answer.toLowerCase() === 'no') {
          count++;
        }
      });
    });
    return count;
  }

  private countTotalQuestions(form: EnrichedForm): number {
    // All activities share the same questions, so count from the first activity only
    let count = 0;
    const firstActivity = form.activities?.[0];
    if (firstActivity) {
      firstActivity.stages?.forEach((stage) => {
        count += stage.questions?.length || 0;
      });
    }
    return count;
  }

  selectActivity(index: number): void {
    this.selectedActivityIndex = index;
    this.cdr.markForCheck();
  }

  get currentActivity(): ActivityData | null {
    return this.selectedForm?.activities?.[this.selectedActivityIndex] || null;
  }

  get hasActivities(): boolean {
    return (this.selectedForm?.activity_count || 0) > 0;
  }

  get allStages(): StageData[] {
    return this.currentActivity?.stages || [];
  }

  get shouldShowOverallInput(): boolean {
    return this.currentActivityHasNoAnswer && this.hasActivities;
  }

  get shouldShowDecisionStatusSection(): boolean {
    return Boolean(
      this.currentActivityHasNoAnswer &&
      this.hasActivities &&
      (this.currentActivityOverallInput.activityStatus === 'approved' ||
       this.currentActivityOverallInput.activityStatus === 'rejected')
    );
  }

  get isApprovedStatus(): boolean {
    return this.currentActivityOverallInput.activityStatus === 'approved';
  }

  get isRejectedStatus(): boolean {
    return this.currentActivityOverallInput.activityStatus === 'rejected';
  }

  get decisionStatusIcon(): string {
    return this.currentActivityOverallInput.activityStatus === 'approved' ? 'check_circle' : 'cancel';
  }

  get decisionStatusText(): string {
    if (this.currentActivityOverallInput.activityStatus === 'approved') {
      return 'Approved';
    }
    if (this.currentActivityOverallInput.isActionRequiredOnReject) {
      return 'Rejected - Action Required';
    }
    return 'Rejected - No Action Required';
  }

  get approverLabel(): string {
    return this.currentActivityOverallInput.activityStatus === 'approved' ? 'Approved by' : 'Rejected by';
  }

  get rootCauseDisplayText(): string {
    return this.currentActivityOverallInput.rootCauseDescription ||
           this.currentActivityOverallInput.rootCause ||
           'Not provided';
  }

  get mitigationActionDisplayText(): string {
    return this.currentActivityOverallInput.mitigationActionDescription ||
           this.currentActivityOverallInput.mitigationAction ||
           'Not provided';
  }

  get monitoringStatusIcon(): string {
    return this.currentActivityOverallInput.confirmed ? 'check_circle' : 'cancel';
  }

  get monitoringStatusText(): string {
    return this.currentActivityOverallInput.confirmed ? 'Confirmed' : 'Not Confirmed';
  }

  get isMonitoringConfirmed(): boolean {
    return Boolean(this.currentActivityOverallInput.confirmed);
  }

  onFilterChange(): void {
    // Re-enrich all questions to apply filter
    this.selectedForm?.activities?.forEach(activity => {
      activity.stages?.forEach(stage => {
        stage.questions?.forEach(question => {
          this.enrichQuestion(question);
        });
      });
    });
    // Trigger change detection when filter changes
    this.cdr.markForCheck();
  }

  enrichQuestion(question: QuestionData): void {
    // Set showQuestion based on filter
    if (this.formData.showOnlyAnswersWithNo) {
      question.showQuestion = Boolean(question.answer && question.answer.toLowerCase() === 'no');
    } else {
      question.showQuestion = true;
    }
  }

  downloadAttachment(question: QuestionData): void {
    if (!question.attachment) {
      this.snackbarService.showError('No file attached to download');
      return;
    }

    const folderPath = this.sharePointService.getCycleFolderPath();
    const fileName = question.attachment;

    this.sharePointService.downloadFile(folderPath, fileName)
      .subscribe({
        next: (blob) => {
          // Create a download link and trigger download
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.snackbarService.showSuccess('File downloaded successfully');
        },
        error: () => {
          this.snackbarService.showError('Failed to download file');
        }
      });
  }

  onBack(): void {
    this.router.navigate(['/sub-manager-cycles'], {
      queryParams: {
        prid: this.managerPrid
      }
    });
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    this.cdr.markForCheck();
  }

  refreshData(): void {
    this.loadCycleFormData();
  }
}
